'use client';

/**
 * DataTable Theme Provider
 *
 * Provides theme context and CSS variable injection for the DataTable component.
 */

import React, { createContext, useContext, useMemo } from 'react';
import { DataTableTheme } from '../types';
import { defaultTheme, mergeTheme, themeToCSS } from './tokens';

interface DataTableThemeContextValue {
  theme: DataTableTheme;
  cssVars: Record<string, string>;
}

const DataTableThemeContext = createContext<DataTableThemeContextValue | null>(null);

interface DataTableThemeProviderProps {
  children: React.ReactNode;
  theme?: Partial<DataTableTheme>;
}

/**
 * Theme provider for DataTable components
 *
 * Wraps DataTable to provide theme context and CSS variables.
 * Can be nested to override themes for specific tables.
 *
 * @example
 * <DataTableThemeProvider theme={{ colors: { bg: { card: '#000' } } }}>
 *   <DataTable {...props} />
 * </DataTableThemeProvider>
 */
export function DataTableThemeProvider({ children, theme }: DataTableThemeProviderProps) {
  const parentContext = useContext(DataTableThemeContext);

  const value = useMemo(() => {
    const baseTheme = parentContext?.theme ?? defaultTheme;
    const mergedTheme = mergeTheme(baseTheme, theme);
    const cssVars = themeToCSS(mergedTheme);

    return {
      theme: mergedTheme,
      cssVars,
    };
  }, [parentContext, theme]);

  return (
    <DataTableThemeContext.Provider value={value}>
      {children}
    </DataTableThemeContext.Provider>
  );
}

/**
 * Hook to access the current DataTable theme
 */
export function useDataTableTheme(): DataTableThemeContextValue {
  const context = useContext(DataTableThemeContext);

  if (!context) {
    // Return default theme if no provider exists
    return {
      theme: defaultTheme,
      cssVars: themeToCSS(defaultTheme),
    };
  }

  return context;
}
